import { trechoDaSessao } from "./sessao";
/**
 * Integração Laboratório ⇆ Portal — navegação entre sistemas com login único
 * (SSO via URL). Os apps rodam localmente em portas diferentes; ao trocar de
 * sistema levamos o usuário logado pela URL (#sso=...) para não pedir login de novo.
 *
 * Laboratório = 4010 · Portal = 8080 · Relatórios (frontend) = 4009.
 */
/** URL do Portal: localhost em dev; apex do dominio em producao (sem hardcode do dominio). */
function portalHost(): string {
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    const isLocal = h === "localhost" || h === "127.0.0.1" || h === "::1";
    if (!isLocal) {
      // O Portal fica no apex; este sistema esta num subdominio -> remove o 1o rotulo.
      const root = h.replace(/^www\./, "").split(".").slice(1).join(".") || h;
      return `${window.location.protocol}//${root}`;
    }
  }
  return "http://localhost:8080";
}
export const SYSTEMS = {
  portal: portalHost(),
  laboratorio: "http://localhost:4010",
  relatorios: "http://localhost:4009",
} as const;

/** systemId deste sistema — usado na checagem de acesso do Portal. */
export const SYSTEM_ID = "laboratorio";

/** Lê o usuário vindo do Portal/outro sistema pela URL (#sso=...). */
export function readSsoUser(): string | null {
  const m = window.location.hash.match(/(?:^|[#&])sso=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Remove o token SSO da URL após o login (deixa a barra de endereço limpa). */
export function clearSsoFromUrl() {
  if (window.location.hash.includes("sso=")) {
    history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

/**
 * Admin Supremo do Portal — login global único: SEMPRE liberado e SEMPRE admin,
 * mesmo sem cadastro local. Comparar sempre em minúsculas, com trim.
 */
export const ADMIN_SUPREMO_LOGIN = "admin@portalsercamp.com";
export const isAdminSupremo = (user: string | null | undefined): boolean =>
  (user ?? "").trim().toLowerCase() === ADMIN_SUPREMO_LOGIN;

/** Navega para outro sistema levando o usuário logado (login único). */
export function goToSystem(url: string, user: string | null) {
  window.location.href = user ? `${url}/#sso=${encodeURIComponent(user)}${trechoDaSessao()}` : url;
}

/** Resultado do portão de acesso do Portal. */
export interface AcessoResult {
  allowed: boolean;
  adminSupremo: boolean;
  /** Acesso somente visualização (cadastro do Portal). */
  somenteLeitura: boolean;
  /** Abas liberadas neste sistema; null = todas. */
  abas: string[] | null;
}

/**
 * Pergunta ao Portal se <user> pode acessar ESTE sistema.
 * GET {PORTAL_URL}/api/acesso?u=<usuario>&sistema=<systemId>
 *   -> { allowed, adminSupremo, somenteLeitura, abas }
 * Lança erro em falha de rede/resposta inválida (o chamador trata de forma segura).
 */
export async function checkAcesso(user: string): Promise<AcessoResult> {
  const url = `${SYSTEMS.portal}/api/acesso?u=${encodeURIComponent(user)}&sistema=${encodeURIComponent(SYSTEM_ID)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Portal respondeu ${res.status}`);
  const body = (await res.json()) as Partial<AcessoResult>;
  return {
    allowed: !!body.allowed,
    adminSupremo: !!body.adminSupremo,
    somenteLeitura: body.somenteLeitura === true,
    abas: body.somenteLeitura === true && Array.isArray(body.abas) ? body.abas.map(String) : null,
  };
}

/** Tipo do aviso de broadcast disparado pelo Admin Cybersecurity no Portal. */
export type NotificacaoTipo = "falha" | "manutencao" | "comunicado";

/** Aviso ativo (incidente, manutenção ou comunicado) transmitido pelo Portal. */
export interface NotificacaoAtiva {
  id: number;
  tipo: NotificacaoTipo;
  titulo: string;
  mensagem: string;
  sistema_alvo: string | null;
  criado_em: string;
  expira_em: string | null;
}

/**
 * Busca os avisos ativos do Portal para ESTE sistema.
 * GET {PORTAL_URL}/api/security/notificacoes/ativas?sistema=<systemId>
 * Endpoint público e somente leitura (sem autenticação). Lança erro em falha
 * de rede/resposta inválida (o chamador trata de forma segura, ex.: mantendo
 * o último estado conhecido).
 */
export async function fetchNotificacoesAtivas(): Promise<NotificacaoAtiva[]> {
  const url = `${SYSTEMS.portal}/api/security/notificacoes/ativas?sistema=${encodeURIComponent(SYSTEM_ID)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Portal respondeu ${res.status}`);
  const body = await res.json();
  return Array.isArray(body) ? (body as NotificacaoAtiva[]) : [];
}
